import { db } from './Database';

export interface LeanDetail {
    id?: number;
    palletLpnNumberId: number;
    variant: string;
    weekType: string | null;
    sideABottom: string;
    sideATop: string;
    sideALean?: string;
    sideBBottom: string;
    sideBTop: string;
    sideBLean?: string;
    sideCBottom: string;
    sideCTop: string;
    sideCLean?: string;
    sideDBottom: string;
    sideDTop: string;
    sideDLean?: string;
    createdAt?: string;
    updatedAt?: string;
}

export class LeanService {
    // Get lean details for a pallet and variant
    static async getLeanDetails(palletId: number, variant: string): Promise<{ palletLpnNumbers: any[] }> {
        try {
            // First get all LPN numbers for the pallet
            const palletLpnNumbers = await db.palletLpnNumbers
                .where('palletId')
                .equals(palletId)
                .toArray();

            if (!palletLpnNumbers || palletLpnNumbers.length === 0) {
                throw new Error('Pallet LPN Numbers not found');
            }

            // Get lean details for each LPN number
            const response = await Promise.all(palletLpnNumbers.map(async palletLpnNumber => {
                // Use a simpler query that filters after fetching
                const leanDetails = await db.leans
                    .where('palletLpnNumberId')
                    .equals(palletLpnNumber.id!)
                    .filter(lean => lean.variant === variant)
                    .toArray();

                return {
                    ...palletLpnNumber,
                    leanDetails: leanDetails || []
                };
            }));

            return {
                palletLpnNumbers: response
            };
        } catch (error) {
            console.error('Error getting lean details:', error);
            throw error;
        }
    }

    // Create or update lean details
    static async createLeanDetails(data: { palletId: number, palletLpnNumbers: any[] }): Promise<{ palletLpnNumbers: any[] }> {
        const { palletId, palletLpnNumbers } = data;

        return await db.transaction('rw', [db.palletLpnNumbers, db.leans], async () => {
            try {
                const createdOrUpdatedLeanDetails = [];
                const timestamp = new Date().toISOString();

                for (const palletLpnNumber of palletLpnNumbers) {
                    const { id, lpnNo, endStatusPallet, leanDetails, ...palletLpnNumberData } = palletLpnNumber;

                    // Create or update PalletLpnNumber
                    let palletLpnNumberRecord;
                    if (id) {
                        await db.palletLpnNumbers.update(id, {
                            lpnNo,
                            endStatusPallet,
                            palletId,
                            ...palletLpnNumberData,
                            updatedAt: timestamp
                        });
                        palletLpnNumberRecord = await db.palletLpnNumbers.get(id);
                    } else {
                        const newId = await db.palletLpnNumbers.add({
                            lpnNo,
                            endStatusPallet,
                            palletId,
                            ...palletLpnNumberData,
                            createdAt: timestamp,
                            updatedAt: timestamp
                        });
                        palletLpnNumberRecord = await db.palletLpnNumbers.get(newId);
                    }

                    if (!palletLpnNumberRecord) continue;

                    // Create or update Lean details
                    const leanDetailsArray = [];
                    for (const leanDetail of leanDetails) {
                        const { id, ...leanData } = leanDetail;

                        if (id) {
                            await db.leans.update(id, {
                                ...leanData,
                                updatedAt: timestamp
                            });
                            const updated = await db.leans.get(id);
                            if (updated) leanDetailsArray.push(updated);
                        } else {
                            const newId = await db.leans.add({
                                ...leanData,
                                palletLpnNumberId: palletLpnNumberRecord.id,
                                createdAt: timestamp,
                                updatedAt: timestamp
                            });
                            const created = await db.leans.get(newId);
                            if (created) leanDetailsArray.push(created);
                        }
                    }

                    createdOrUpdatedLeanDetails.push({
                        ...palletLpnNumberRecord,
                        leanDetails: leanDetailsArray
                    });
                }

                return {
                    message: 'Success',
                    palletLpnNumbers: createdOrUpdatedLeanDetails
                };
            } catch (error) {
                console.error('Error creating/updating lean details:', error);
                throw error;
            }
        });
    }
}
